import { Link, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { marketingForSlug, type MarketingBlock } from "../content/marketingPages";
import { pathnameToPageSlug } from "../utils/path";
import { BuyPosForm } from "../components/BuyPosForm";
import { BuyServerForm } from "../components/BuyServerForm";
import { ScheduleConsultButton } from "../components/ScheduleConsultButton";
import { TrainModelForm } from "../components/TrainModelForm";
import { LegalDocumentPage } from "./LegalDocumentPage";

function MarketingLayout({ block, children }: { block: MarketingBlock; children?: ReactNode }) {
  const consultCta =
    (block.ctaLabel || "").toLowerCase().includes("consultation") ||
    (block.ctaHref || "") === "#contact";

  return (
    <article className="page">
      <p className="eyebrow">{block.eyebrow}</p>
      <h1>{block.title}</h1>
      <p className="page-summary">{block.summary}</p>
      <ul className="marketing-bullets">
        {block.bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
      {children}
      {block.ctaLabel && (consultCta || block.ctaHref) && (
        <p className="hero-actions" style={{ marginTop: "1.5rem" }}>
          {consultCta ? (
            <ScheduleConsultButton
              className="btn btn-schedule-consult"
              label={block.ctaLabel}
            />
          ) : block.ctaHref!.startsWith("http") ? (
            <a className="btn btn-primary" href={block.ctaHref} target="_blank" rel="noreferrer">
              {block.ctaLabel}
            </a>
          ) : block.ctaHref!.startsWith("#") ? (
            <a className="btn btn-primary" href={block.ctaHref}>
              {block.ctaLabel}
            </a>
          ) : (
            <Link className="btn btn-primary" to={block.ctaHref!}>
              {block.ctaLabel}
            </Link>
          )}
        </p>
      )}
    </article>
  );
}

export function MarketingPage() {
  const { pathname } = useLocation();
  const slug = pathnameToPageSlug(pathname);

  if (slug === "terms" || slug === "code-of-conduct" || slug === "privacy") {
    return <LegalDocumentPage doc={slug} />;
  }

  const block = marketingForSlug(slug);

  if (!block) {
    return (
      <article className="page">
        <p className="eyebrow">TNK Main</p>
        <h1>Page not found</h1>
        <p className="page-summary">Try a link from the menu above or return home.</p>
        <Link to="/" className="card-link">
          ← Home
        </Link>
      </article>
    );
  }

  if (slug === "web" || slug === "web/servers") {
    return (
      <article className="page">
        <section className="deposit-section">
          <p className="eyebrow">Web</p>
          <h1 className="deposit-heading">Buy a small web server</h1>
          <p className="action-form-lead">
            Dedicated AWS small to host your website. Pay a refundable deposit to get started.
          </p>
          <BuyServerForm lockedProduct="website_server" />
        </section>
      </article>
    );
  }

  if (slug === "pos") {
    return (
      <article className="page">
        <section className="deposit-section">
          <p className="eyebrow">POS</p>
          <h1 className="deposit-heading">15 Day Refundable Free-Trial (POS)</h1>
          <p className="action-form-lead">
            Just fill out the form and you&apos;ll be alerted when the setup is complete.
          </p>
          <BuyPosForm lockedProduct="point_of_sale" />
        </section>
      </article>
    );
  }

  if (slug === "cart") {
    return (
      <article className="page">
        <section className="deposit-section">
          <p className="eyebrow">Cart</p>
          <h1 className="deposit-heading">15 Day Refundable Free-Trial (Ecommerce cart)</h1>
          <p className="action-form-lead">
            Just fill out the form and you&apos;ll be alerted when the setup is complete.
          </p>
          <BuyPosForm lockedProduct="ecommerce_cart" />
        </section>
      </article>
    );
  }

  if (slug === "ai-models/train") {
    return (
      <article className="page page--wide">
        <p className="eyebrow">AI Models</p>
        <h1 className="deposit-heading">Convert Your Data into AI Models</h1>
        <TrainModelForm lockedPath="train" />
      </article>
    );
  }

  if (slug === "ai-agents/ask") {
    return (
      <article className="page page--wide">
        <p className="eyebrow">AI Agents</p>
        <h1 className="deposit-heading">Make Agent Analyze, Search your files</h1>
        <TrainModelForm lockedPath="agent" />
      </article>
    );
  }

  return <MarketingLayout block={block} />;
}
